/* eslint-disable */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Form } from 'formik';
import PatientInfoSection from './PatientInfoSection';
import TreatmentTypeSelector from './TreatmentTypeSelector';
import SCITDetails from './SCITDetails';
import SLITDetails from './SLITDetails';
import AdditionalInfoSection from './AdditionalInfoSection';
import DeliveryInfoSection from './DeliveryInfoSection';
import FormikSelectField from 'components/FormikFields/FormikSelectField';
import { orderTypes } from 'components/Orders/constants';
import { checkPermission } from '../../../../helpers/checkPermissions';
import permissions from '../../../../config/permissions';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { checkImmunotherapyPermission, setCanEnableImmunotherapyReport } from '../../../../redux_store';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingRight: theme.spacing(2),
  },
  section: {
    width: '100%',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors?.lightBlue || '#5B65DC'}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1),
    },
  },
  sectionContent: {
    marginTop: theme.spacing(2),
    maxWidth: 1000,
    width: '100%',
  },
  formGroup: {
    marginBottom: theme.spacing(4),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  groupTitle: {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  groupContent: {
    paddingLeft: theme.spacing(2),
  },
  fullWidth: {
    width: '100%',
    maxWidth: 'none',
    marginTop: 0,
  },
}));

const OrderForm = ({
                     values,
                     backendErrors,
                     metadata: { patientInfo, addresses, providers },
                     isEditable,
                   }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { accountUuid } = useParams();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const {
    metadata: { currentAccountPermissions, currentAccount },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const {canEnableImmunotherapyReport} = useSelector((state) => state.order);
  const providerItems = providers.map((provider) => ({
    label: provider.name,
    value: provider.uuid,
  }));

  useEffect(() => {
    if (isDistributionSponsor) {
      dispatch(checkImmunotherapyPermission(accountUuid || currentAccount.uuid));
    } else {
      const hasPermission = checkPermission(
        currentAccountPermissions,
        permissions.IMMUNOTHERAPY_REPORT,
      );
      dispatch(setCanEnableImmunotherapyReport(hasPermission));
    }
  }, [isDistributionSponsor]);

  return (
    <Form>
      <Grid container direction='column' className={classes.root}>
        <PatientInfoSection patientInfo={patientInfo} />

        {/* Order Details Section */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {forms.orderDetails || 'Order Details'}
          </Typography>
          <div className={classes.sectionContent}>
            {/* Provider Selection Group */}
            <div className={classes.formGroup}>
              <Typography className={classes.groupTitle}>
                Provider Information
              </Typography>
              <div className={classes.groupContent}>
                <FormikSelectField
                  name='providerUserUUID'
                  label={forms.provider}
                  type='text'
                  menuItems={providerItems}
                  required
                  backendError={backendErrors.providerUserUUID}
                  className={classes.fullWidth}
                  disabled={!isEditable}
                  hideErrorAreaIfNoErrors
                />
              </div>
            </div>

            <TreatmentTypeSelector
              isEditable={isEditable}
              backendError={backendErrors.category}
            />

            {/* Treatment Details Group */}
            {!!values.category && (
              <div className={classes.formGroup}>
                <Typography className={classes.groupTitle}>
                  Treatment Details
                </Typography>
                <div className={classes.groupContent}>
                  {values.category === orderTypes.SCIT && (
                    <SCITDetails
                      values={values}
                      backendErrors={backendErrors}
                      isEditable={isEditable}
                      canEnableImmunotherapyReport={canEnableImmunotherapyReport}
                    />
                  )}
                  {values.category === orderTypes.SLIT && (
                    <SLITDetails
                      backendErrors={backendErrors}
                      isEditable={isEditable}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <AdditionalInfoSection
          backendError={backendErrors.comments}
          isEditable={isEditable}
        />

        <DeliveryInfoSection
          addresses={addresses}
          isEditable={isEditable}
        />
      </Grid>
    </Form>
  );
};

OrderForm.propTypes = {
  values: PropTypes.shape({
    category: PropTypes.string,
    scitType: PropTypes.string,
    slitLowDoseQuantity: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    slitMaintenanceQuantity: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
  }).isRequired,
  backendErrors: PropTypes.shape({
    providerUserUUID: PropTypes.string,
    category: PropTypes.string,
    scitType: PropTypes.string,
    scitAllergens: PropTypes.string,
    address: PropTypes.string,
    comments: PropTypes.string,
    slitLowDoseQuantity: PropTypes.string,
    slitMaintenanceQuantity: PropTypes.string,
    allergySymptoms: PropTypes.string,
    symptomsExposure: PropTypes.string,
    symptomsTimeOfYear: PropTypes.string,
    symptomsTimeInDay: PropTypes.string,
    symptomsMostlyOccur: PropTypes.string,
    examinationReveals: PropTypes.string,
    allergyDiagnosis: PropTypes.string,
  }),
  metadata: PropTypes.shape({
    patientInfo: PropTypes.object,
    addresses: PropTypes.array,
    providers: PropTypes.array,
  }).isRequired,
  isEditable: PropTypes.bool,
};

OrderForm.defaultProps = {
  backendErrors: undefined,
  isEditable: true,
};

export default OrderForm;
